import React, { useState, useEffect } from 'react';
import { schedulesService, screensService, groupsService } from '../services/api';
import ScheduleModal from '../components/ScheduleModal';

function Schedules({ user }) {
  const [schedules, setSchedules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [schedulesRes, screensRes, groupsRes] = await Promise.all([
        schedulesService.getAll(),
        screensService.getAll(),
        groupsService.getAll()
      ]);
      setSchedules(schedulesRes.data);
      setScreens(screensRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSchedule(null);
    setShowModal(true);
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette programmation ?')) {
      return;
    }

    try {
      await schedulesService.delete(scheduleId);
      setMessage('Programmation supprimée');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('Erreur lors de la suppression');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSave = async (scheduleData) => {
    try {
      if (editingSchedule) {
        await schedulesService.update(editingSchedule.id, scheduleData);
        setMessage('Programmation modifiée');
      } else {
        await schedulesService.create(scheduleData);
        setMessage('Programmation créée');
      }
      setTimeout(() => setMessage(''), 3000);
      setShowModal(false);
      loadData();
    } catch (error) {
      setMessage('' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const getTargetName = (schedule) => {
    if (schedule.screen_id) {
      const screen = screens.find(s => s.id === schedule.screen_id);
      return screen ? screen.name : 'Écran inconnu';
    }
    if (schedule.group_id) {
      const group = groups.find(g => g.id === schedule.group_id);
      return group ? `Groupe: ${group.name}` : 'Groupe inconnu';
    }
    return 'Non défini';
  };

  const getActionLabel = (action) => {
    const labels = {
      'tv_on': 'Allumer TV',
      'tv_off': '🌙 Éteindre TV',
      'change_url': '🌐 Changer URL',
      'change_playlist': 'Changer playlist'
    };
    return labels[action] || action;
  };

  const getScheduleTypeLabel = (type) => {
    const labels = {
      'once': 'Une fois',
      'daily': '🔄 Quotidien',
      'weekly': '📆 Hebdomadaire'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="schedules-page">
      <div className="page-header">
        <h1>Programmation horaire</h1>
        <button onClick={handleCreate} className="btn-secondary">
          Créer une programmation
        </button>
      </div>

      {message && <div className="message-banner">{message}</div>}

      <div className="schedules-list">
        {schedules.map(schedule => (
          <div key={schedule.id} className={`schedule-card ${schedule.is_active ? '' : 'inactive'}`}>
            <div className="schedule-header">
              <h3>{schedule.name}</h3>
              <div className="schedule-badges">
                {!schedule.is_active && (
                  <span className="status-badge inactive">Inactive</span>
                )}
                <span className="type-badge">{getScheduleTypeLabel(schedule.schedule_type)}</span>
              </div>
            </div>

            <div className="schedule-info">
              <div className="info-row">
                <span className="label">Cible:</span>
                <span className="value">{getTargetName(schedule)}</span>
              </div>
              <div className="info-row">
                <span className="label">Action:</span>
                <span className="value">{getActionLabel(schedule.action)}</span>
              </div>
              <div className="info-row">
                <span className="label">Heure:</span>
                <span className="value">{schedule.start_time}</span>
              </div>
              {schedule.schedule_type === 'weekly' && schedule.days_of_week && (
                <div className="info-row">
                  <span className="label">Jours:</span>
                  <span className="value">
                    {schedule.days_of_week.split(',').map(day => {
                      const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
                      return days[parseInt(day) - 1];
                    }).join(', ')}
                  </span>
                </div>
              )}
              {schedule.schedule_type === 'once' && schedule.specific_date && (
                <div className="info-row">
                  <span className="label">Date:</span>
                  <span className="value">{schedule.specific_date}</span>
                </div>
              )}
            </div>

            <div className="schedule-actions">
              <button onClick={() => handleEdit(schedule)} className="btn-edit">
                ✏️ Modifier
              </button>
              <button onClick={() => handleDelete(schedule.id)} className="btn-delete">
                🗑️ Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>

      {schedules.length === 0 && (
        <div className="empty-state">
          <p>Aucune programmation horaire pour le moment.</p>
          <p className="help-text">
            Les programmations permettent d'automatiser des actions (allumer/éteindre TV, changer URL...) à des horaires définis.
          </p>
        </div>
      )}

      {showModal && (
        <ScheduleModal
          schedule={editingSchedule}
          screens={screens}
          groups={groups}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default Schedules;
